import { useState, useEffect, useCallback, useRef } from 'react';
import { getAssignments, createAssignment, deleteAssignment, getLatestSignoff, createSignoff, removeSignoff, logActivity } from '../lib/supabase';
import { daysUntil, nextSchoolDay, stepsFromAssignment } from '../lib/underground';
import LineMap from '../components/LineMap';
import AssignmentCard from '../components/AssignmentCard';
import AssignmentModal from '../components/AssignmentModal';
import roundelSrc from '../assets/roundel.svg';
import wayOutSrc from '../assets/wayout.svg';

// Inject planner animation styles once
(function ensurePlannerStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('planner-anim-styles')) return;
  const s = document.createElement('style');
  s.id = 'planner-anim-styles';
  s.textContent = `
    @keyframes planner-fade-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .planner-list-fade {
      animation: planner-fade-in 0.15s ease both;
    }
    .planner-card-stagger {
      opacity: 0;
      animation: planner-fade-in 0.25s ease forwards;
    }
  `;
  document.head.appendChild(s);
}());

const FILTERS = [
  { id: 'all',      label: 'All',        color: '#003688', rule: 'Active assignments' },
  { id: 'overdue',  label: 'Overdue',    color: '#7b0000', rule: 'Past due date' },
  { id: 'urgent',   label: 'Urgent',     color: '#e32017', rule: '2 days or less' },
  { id: 'upcoming', label: 'Due Soon',   color: '#c68a00', rule: '3-6 days' },
  { id: 'distant',  label: 'Next Week+', color: '#00782a', rule: '7 or more days' },
  { id: 'done',     label: 'Done',       color: '#6f7375', rule: 'All steps ticked' },
];

function getFilterId(assignment) {
  const steps = assignment.steps || [];
  const allDone = steps.length > 0 && steps.every(s => s.done);
  if (allDone) return 'done';
  const d = daysUntil(assignment.due_date);
  if (d < 0) return 'overdue';
  if (d <= 2) return 'urgent';
  if (d <= 6) return 'upcoming';
  return 'distant';
}

function WayOutButton({ onClick, style }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <button
      className="btn btn-ghost btn-sm"
      style={{ padding: '2px 4px', background: 'none', border: 'none', cursor: 'pointer', ...style }}
      onClick={onClick}
    >
      {imgFailed ? (
        <span style={{ color: 'white', fontSize: 13, fontWeight: 700, border: '1px solid rgba(255,255,255,0.4)', padding: '4px 10px', borderRadius: 6 }}>Way out</span>
      ) : (
        <img
          src={wayOutSrc}
          alt="Way out"
          height="28"
          style={{ display: 'block' }}
          onError={() => setImgFailed(true)}
        />
      )}
    </button>
  );
}

export default function StudentPlanner({ student, isAdmin, isStudent, userId, studentDbId, onSignOut, onBack, onStudentSignOut, isTeacher }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [flashFilter, setFlashFilter] = useState(false);
  const [listFadeKey, setListFadeKey] = useState(0);
  const [cardStaggerKey, setCardStaggerKey] = useState(0);
  const listRef = useRef(null);
  // Copies being made right now, by name|subject|due date. Checked synchronously
  // so two taps for the same task can never both insert, whatever React has rendered.
  const repeatInFlight = useRef(new Set());
  // A teacher's sign-off. undefined = not known yet (loading, or the last read failed);
  // null = never signed off. SIGN OFF stays disabled until it is known, so a
  // slow or failed read can never lead to a second sign-off.
  const [latestSignoff, setLatestSignoff] = useState(undefined);
  const [signoffError, setSignoffError] = useState(false);
  const [signoffMsg, setSignoffMsg] = useState('');
  const [signingOff, setSigningOff] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAssignments(student.id);
      setAssignments(data);
      setCardStaggerKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => { load(); }, [load]);

  const loadLatestSignoff = useCallback(async () => {
    try {
      const s = await getLatestSignoff(student.id);
      setLatestSignoff(s);
      setSignoffError(false);
    } catch (e) {
      console.error('getLatestSignoff:', e);
      setLatestSignoff(undefined);
      setSignoffError(true);
    }
  }, [student.id]);

  useEffect(() => {
    if (!isTeacher) return;
    setLatestSignoff(undefined);
    setSignoffError(false);
    loadLatestSignoff();
  }, [isTeacher, loadLatestSignoff]);

  // While it is not known (a read failed, or is slow), look again every minute.
  useEffect(() => {
    if (!isTeacher || latestSignoff !== undefined) return;
    const interval = setInterval(loadLatestSignoff, 60000);
    return () => clearInterval(interval);
  }, [isTeacher, latestSignoff, loadLatestSignoff]);

  useEffect(() => {
    const interval = setInterval(() => { load(); }, 60000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (searchQuery.trim()) {
      setTimeout(() => {
        listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [searchQuery]);

  function scrollToList(filterId) {
    setActiveFilter(filterId);
    setFlashFilter(true);
    setListFadeKey(k => k + 1);
    setTimeout(() => setFlashFilter(false), 900);
    setTimeout(() => {
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  const counts = FILTERS.reduce((acc, f) => {
    acc[f.id] = f.id === 'all'
      ? assignments.filter(a => getFilterId(a) !== 'done').length
      : assignments.filter(a => getFilterId(a) === f.id).length;
    return acc;
  }, {});

  const filtered = activeFilter === 'all'
    ? assignments.filter(a => getFilterId(a) !== 'done')
    : assignments.filter(a => getFilterId(a) === activeFilter);

  const searched = searchQuery.trim()
    ? filtered.filter(a =>
        a.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
        (a.subject || '').toLowerCase().includes(searchQuery.trim().toLowerCase())
      )
    : filtered;

  const sorted = [...searched].sort((a, b) => {
    if (activeFilter === 'done') return new Date(b.due_date) - new Date(a.due_date);
    return daysUntil(a.due_date) - daysUntil(b.due_date);
  });

  const activeCount = assignments.filter(a => getFilterId(a) !== 'done').length;
  const doneCount = counts['done'];

  const signedOffToday = latestSignoff && new Date(latestSignoff.signed_off_at).toDateString() === new Date().toDateString();

  async function handleSignoff() {
    if (!student?.id || latestSignoff === undefined) return;
    setSigningOff(true);
    try {
      if (signedOffToday) {
        await removeSignoff(latestSignoff.id);
        setSignoffMsg('Sign-off removed.');
        // Whether an earlier sign-off is now the latest is the database's to say.
        setLatestSignoff(undefined);
        loadLatestSignoff();
      } else {
        // The new row itself, not a second read that could fail.
        const created = await createSignoff(student.id, userId);
        setLatestSignoff(created);
        setSignoffMsg('✅ Signed off!');
      }
    } catch { setSignoffMsg('Something went wrong — try again.'); }
    finally { setSigningOff(false); }
  }

  async function handleDelete(id, name) {
    if (!window.confirm('Remove this assignment?')) return;
    await deleteAssignment(id);
    // A student's removal leaves one row on the parent's Activity tab. The
    // removal has already happened, so a failed log write must not disturb it.
    if (isStudent) {
      try {
        await logActivity(student.id, 'assignment_removed', `${name} removed by ${student.name}`);
      } catch (e) {
        console.error('logActivity:', e);
      }
    }
    load();
  }

  // "Again tomorrow": copy a card to the next school day, steps unticked, using
  // the same insert the add form uses. Answers 'added', 'exists' or 'failed'.
  // 'exists' is decided from the list already loaded (no new query): a row with
  // the same name, subject and due date means the copy is already on the map.
  async function handleRepeat(a) {
    const due = nextSchoolDay();
    if (assignments.some(x => x.name === a.name && x.subject === a.subject && x.due_date === due)) return 'exists';
    const key = `${a.name}|${a.subject}|${due}`;
    if (repeatInFlight.current.has(key)) return 'exists';
    repeatInFlight.current.add(key);
    try {
      const addedBy = isStudent ? studentDbId : userId;
      const steps = stepsFromAssignment(a).map(s => ({ label: s.label, done: false }));
      await createAssignment(student.id, { name: a.name, subject: a.subject, color: a.color, due_date: due }, steps, addedBy, isStudent);
      await load();
      return 'added';
    } catch (e) {
      console.error('repeat:', e);
      return 'failed';
    } finally {
      repeatInFlight.current.delete(key);
    }
  }

  function handleEdit(a) {
    setEditingAssignment(a);
    setShowModal(true);
  }

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const canAdd = isAdmin || isStudent;

  const TOP_SIGNS = [
    { id: 'urgent',   label: 'URGENT',     color: '#e32017', rule: '2 days or less', arrow: 'up' },
    { id: 'upcoming', label: 'DUE SOON',   color: '#c68a00', rule: '3-6 days',       arrow: 'right' },
    { id: 'distant',  label: 'NEXT WEEK+', color: '#00782a', rule: '7 or more days', arrow: 'right' },
  ];
  const BOTTOM_SIGNS = [
    { id: 'overdue', label: 'OVERDUE', color: '#7b0000', rule: 'Past due date',      arrow: '!' },
    { id: 'all',     label: 'ALL',     color: '#003688', rule: 'Active assignments', arrow: 'down' },
    { id: 'done',    label: 'DONE',    color: '#6f7375', rule: 'All steps ticked',  arrow: 'check' },
  ];

  function SignArrow({ type }) {
    if (type === 'up') return <svg width="16" height="16" viewBox="0 0 16 16"><path d="M8 13V3M3 8l5-5 5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>;
    if (type === 'right') return <svg width="16" height="16" viewBox="0 0 16 16"><path d="M3 8h10M8 3l5 5-5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>;
    if (type === 'down') return <svg width="16" height="16" viewBox="0 0 16 16"><path d="M8 3v10M3 8l5 5 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>;
    if (type === 'check') return <svg width="16" height="16" viewBox="0 0 16 16"><path d="M3 8l4 4 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>;
    return <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>{type}</span>;
  }

  function Sign({ f }) {
    return (
      <div
        onClick={() => scrollToList(f.id)}
        style={{
          borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
          outline: activeFilter === f.id ? `2px solid ${f.color}` : 'none',
          outlineOffset: 1,
        }}>
        <div style={{ background: f.color, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: 'white', fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', fontFamily: 'p22-underground, Arial' }}>{f.label}</span>
          <SignArrow type={f.arrow} />
        </div>
        <div style={{ background: 'white', padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <span style={{ fontSize: 26, fontWeight: 700, color: f.color, lineHeight: 1, fontFamily: 'p22-underground, Arial' }}>{counts[f.id]}</span>
          <span style={{ fontSize: 8, color: '#888', textAlign: 'right', maxWidth: 70, lineHeight: 1.3 }}>{f.rule}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="app-header">
        <img
          src={roundelSrc}
          className="roundel"
          alt="Homework Underground"
          width={48}
          height={48}
          style={{ flexShrink: 0 }}
          onError={e => {
            e.target.outerHTML = '<svg class="roundel" viewBox="0 0 52 52" style="width:48px;height:48px;flex-shrink:0"><circle cx="26" cy="26" r="24" fill="#e32017"/><rect x="2" y="19" width="48" height="14" fill="#003688"/><text x="26" y="29" text-anchor="middle" font-size="8" fill="white" font-weight="700" font-family="Arial">HOMEWORK</text></svg>';
          }}
        />
        <div>
          <h1>HOMEWORK UNDERGROUND</h1>
          <p>{student.name}'s Planner{isAdmin && <span style={{ fontSize: 10, opacity: 0.6, marginLeft: 6, fontWeight: 400 }}>· Viewing as {isTeacher ? 'teacher' : 'Admin'}</span>}</p>
        </div>
        <div className="header-right">
          {isAdmin && onBack && (
            <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginLeft: 'auto' }}>
              ← Back to Admin
            </button>
          )}
          {isStudent && <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', fontWeight: 700 }}>Hi, {student.name}</span>}
          {isStudent && onStudentSignOut && (
            <WayOutButton onClick={onStudentSignOut} />
          )}

          {isAdmin && onSignOut && (
            <WayOutButton onClick={onSignOut} />
          )}
        </div>
      </div>

      {/* Red bar — search + date/counts */}
      <div style={{ background: 'var(--red)', padding: '5px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <svg width="13" height="13" viewBox="0 0 13 13" style={{ position: 'absolute', left: 9, opacity: 0.85, zIndex: 1 }}>
            <circle cx="5" cy="5" r="4" fill="none" stroke="white" strokeWidth="1.5"/>
            <line x1="8" y1="8" x2="12" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search assignments..." className="redbar-search" onFocus={e => e.target.style.opacity=1} onBlur={e => e.target.style.opacity=1}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              background: 'rgba(255,255,255,0.25)', border: '1.5px solid rgba(255,255,255,0.75)',
              borderRadius: 20, padding: '5px 30px 5px 28px',
              fontSize: 12, color: 'white', width: 200, outline: 'none', caretColor: 'white',
            }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}
              style={{ position: 'absolute', right: 8, background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1 }}>
              x
            </button>
          )}
        </div>
        <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>{today} · {activeCount} active · {doneCount} complete</span>
      </div>
      </div>

      {/* TfL Sign counters */}
      <div style={{ padding: '20px 0 10px', background: 'var(--bg)' }}>
        <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 14 }}>
            {TOP_SIGNS.map(f => <Sign key={f.id} f={f} />)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {BOTTOM_SIGNS.map(f => <Sign key={f.id} f={f} />)}
          </div>
        </div>
      </div>

      <div className="main">

        {/* Line map — forward-looking only, overdue items excluded.
            Overdue assignments are handled by the counter grid and card tape stripes. */}
        {activeFilter !== 'done' && assignments.filter(a => daysUntil(a.due_date) >= 0 && getFilterId(a) !== 'done').length > 0 && (
          <>
            <div style={{ position: 'relative', paddingTop: 14 }}>
              {/* LINE MAP station sign — straddles the map's top border.
                  Replaces the old in-SVG navy strip + title. Matches the Sign
                  counter style (navy, white P22, radius 6). */}
              <div style={{
                position: 'absolute', top: 0, left: 24, zIndex: 5,
                background: '#003688', color: 'white',
                padding: '6px 16px', borderRadius: 6,
                fontSize: 12, fontWeight: 700, letterSpacing: '1.5px',
                fontFamily: 'p22-underground, Arial',
                boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                pointerEvents: 'none',
              }}>
                LINE MAP
              </div>
              <LineMap
                assignments={assignments.filter(a => daysUntil(a.due_date) >= 0 && getFilterId(a) !== 'done')}
              />
            </div>
          </>
        )}

        {/* Pill filter tabs + Add Assignment text link */}
        <div ref={listRef} style={{ paddingBottom: 4, transition: 'box-shadow 0.4s', boxShadow: flashFilter ? '0 0 0 3px rgba(0,54,136,0.2)' : 'none', borderRadius: 4 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {FILTERS.map(f => {
              const count = counts[f.id];
              const active = activeFilter === f.id;
              const tint = active ? f.color : `${f.color}18`;
              return (
                <button key={f.id} onClick={() => { setActiveFilter(f.id); setListFadeKey(k => k + 1); }} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 14px', borderRadius: 20,
                  border: `2px solid ${f.color}`,
                  background: active ? f.color : tint,
                  color: active ? 'white' : f.color,
                  fontSize: 13, fontWeight: 700,
                  fontFamily: 'p22-underground, Arial',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  {f.label}
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                    background: active ? 'rgba(255,255,255,0.25)' : count > 0 ? f.color : 'rgba(0,0,0,0.1)',
                    color: active ? 'white' : count > 0 ? 'white' : f.color,
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {canAdd && (
            <button
              onClick={() => { setEditingAssignment(null); setShowModal(true); }}
              style={{ background: 'none', border: 'none', color: '#003688', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '2px 0', fontFamily: 'p22-underground, Arial' }}>
              + Add Assignment
            </button>
          )}
        </div>

        {/* Section label + search result count */}
        <div id="assignment-list" className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {activeFilter === 'all'      && 'All active - sorted by urgency'}
            {activeFilter === 'overdue'  && 'Overdue - needs attention now'}
            {activeFilter === 'urgent'   && 'Urgent - due within 2 days'}
            {activeFilter === 'upcoming' && 'Due Soon - 3 to 6 days'}
            {activeFilter === 'distant'  && 'Next Week+ - 7 or more days away'}
            {activeFilter === 'done'     && 'Done - all steps ticked'}
          </span>
          {searchQuery.trim() && (
            <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>
              {sorted.length} result{sorted.length !== 1 ? 's' : ''} for "{searchQuery.trim()}"
            </span>
          )}
        </div>

        {loading && <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>Loading...</div>}

        <div key={listFadeKey} className="planner-list-fade">
          {!loading && sorted.length === 0 && (
            <div className="empty-state">
              <div className="icon">{activeFilter === 'done' ? '🎉' : '🚉'}</div>
              <p>
                {searchQuery.trim() ? `No results for "${searchQuery.trim()}"` :
                 activeFilter === 'done' ? 'No completed assignments yet.' :
                 activeFilter === 'all'  ? 'No active assignments.' :
                 'No assignments in this category.'}
              </p>
            </div>
          )}

          {sorted.map((a, idx) => (
            <div
              key={`${cardStaggerKey}-${a.id}`}
              className="planner-card-stagger"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <AssignmentCard
                assignment={a}
                onUpdated={load}
                isAdmin={isAdmin}
                isStudent={isStudent}
                studentDbId={studentDbId}
                onEdit={() => handleEdit(a)}
                onDelete={() => handleDelete(a.id, a.name)}
                onRepeat={canAdd ? handleRepeat : undefined}
              />
            </div>
          ))}
        </div>

        {/* Teacher sign-off — bottom of list */}
        {isTeacher && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {signedOffToday ? '✅ Signed off today' : 'Ready to sign off?'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                {latestSignoff === undefined
                  ? (signoffError ? "Couldn't check just now" : 'Checking…')
                  : signedOffToday ? "Tap to undo today's sign-off" : "Confirm you've reviewed the assignments"}
              </div>
              {signoffMsg && <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4 }}>{signoffMsg}</div>}
            </div>
            <button
              onClick={handleSignoff}
              disabled={signingOff || latestSignoff === undefined}
              style={{
                background: 'none', border: 'none', cursor: signingOff || latestSignoff === undefined ? 'default' : 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 4, flexShrink: 0, padding: '4px 8px',
                opacity: signingOff || latestSignoff === undefined ? 0.5 : 1, transition: 'opacity 0.2s',
              }}
              title={signedOffToday ? "Tap to undo today's sign-off" : 'Sign off'}>
              <svg width="44" height="44" viewBox="0 0 44 44" style={{ transition: 'all 0.25s' }}>
                {signedOffToday ? (
                  <>
                    <circle cx="22" cy="22" r="18" fill="#00782a"/>
                    <polyline points="13,22 19,28 31,15" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                ) : (
                  <circle cx="22" cy="22" r="18" fill="none" stroke="#aaa" strokeWidth="2.5"/>
                )}
              </svg>
              <span style={{ fontSize: 10, fontWeight: 700, color: signedOffToday ? '#00782a' : 'var(--muted)', letterSpacing: '0.5px', fontFamily: 'p22-underground, Arial' }}>
                {signedOffToday ? 'SIGNED OFF' : 'SIGN OFF'}
              </span>
            </button>
          </div>
        )}

        {sorted.length > 2 && isAdmin && onBack && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <button
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              onClick={onBack}>
              Back to Admin
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <AssignmentModal
          studentId={student.id}
          assignment={editingAssignment}
          userId={userId}
          isStudent={isStudent}
          studentDbId={studentDbId}
          onSaved={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
